import createFetchClient from 'openapi-fetch'
import createClient, { type MethodResponse } from 'openapi-react-query'
import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers'
import type { paths } from 'user-api/src/generated/openApi/schema'

const isEmptyQueryValue = (value: unknown) =>
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value)) ||
    (Array.isArray(value) && value.length === 0)

/**
 * Sorts params and drops empty ones so the same logical request always produces
 * the same url. Vercel's CDN keys on the url, so unsorted or empty-valued params
 * fragment the cache. Arrays stay as repeated keys (`?a=1&a=2`), which is what
 * the api's `toStringArray` preprocessors expect.
 */
const stableQuerySerializer = (query: Record<string, unknown>) =>
    Object.entries(query)
        .filter(([, value]) => !isEmptyQueryValue(value))
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .flatMap(([key, value]) =>
            Array.isArray(value)
                ? value
                      .filter((item) => !isEmptyQueryValue(item))
                      .map((item) => `${key}=${encodeURIComponent(String(item))}`)
                : [`${key}=${encodeURIComponent(String(value))}`]
        )
        .join('&')

export const fetchClient = createFetchClient<paths>({
    baseUrl: process.env.NEXT_PUBLIC_RESTAURANT_API_URL,
    headers: {
        // The browser forbids setting User-Agent, so only tag server-side traffic.
        ...(typeof window === 'undefined' ? { 'User-Agent': 'NeotasteWeb' } : {}),
    },
    querySerializer: stableQuerySerializer,
})

export const userApiClient = createClient(fetchClient)

/**
 * Response body for a spec path, as the client actually returns it.
 *
 * Deliberately not `paths[P][M]['responses'][200][...]`: openapi-fetch runs the
 * response through `Readable<>`, whose key filter is
 * `NonNullable<T[K]> extends $Write<any> ? never : K`. A property typed exactly
 * `null` narrows to `never`, `never extends` anything is true, and the key is
 * silently dropped. Deriving straight from `paths` would therefore produce
 * aliases that no query result is assignable to.
 */
export type ApiResponseBody<
    M extends HttpMethod,
    P extends PathsWithMethod<paths, M>,
> = MethodResponse<typeof userApiClient, M, P>
